import { Box, Button, Dropdown, Input, Table, TopBar } from '@sam/components';
import { TableHeader, TableRow } from '@sam/components/src/Table/Table.types';
import { useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  createNewCategory,
  generateNotification,
  generateTranslationTableRows,
  InventoryCategory,
  InventoryGroup,
  InventoryMetaType,
  InventroyMetaInformation,
  Language,
  updateCategory,
  useData,
} from 'shared';
import { NotificationType } from 'shared/src/notification/notification.types';
import { SaveButtons } from '../../components/saveButtons/SaveButtons';
import { useUser } from '../../components/UserContext';
import {
  convertMetaInformationIntoTableRows,
  generateEmptyCategory,
} from '../../utils/inventory/Inventory.utils';
import { getUserNameForSimpleUser } from '../../utils/user/User.utils';
import { uid } from 'uid';
import { generateTableRowForLanguageTranslation } from '../../utils/InputUtils';
import { IconComponent, IconType } from '../../components/icons/IconComponent';

export const InventoryCategoryEdit: React.FC = () => {
  const { t } = useTranslation();
  const { axios, user } = useUser();
  const navigate = useNavigate();
  const location = useLocation<{ category?: InventoryCategory }>();
  const button = useRef<HTMLButtonElement>(null);
  const form = useRef<HTMLFormElement>(null);
  const [category, setCategory] = useState<InventoryCategory>(
    location.state?.category
      ? location.state.category
      : generateEmptyCategory({ createdBy: user.id }),
  );
  const [metaInfoToEdit, setMetaInfoToEdit] = useState<InventroyMetaInformation>();

  const { data: loadedUsers } = useData('SIMPLEUSERS_ALL', {
    config: { fallbackData: [] },
  });

  /**
   * Submit handler method to update or create a category
   * @param redirect if true user is navigated back
   */
  const handleSubmit = (redirect?: boolean): void => {
    button.current?.click();
    const promise: Promise<InventoryCategory | undefined> = category.id
      ? updateCategory(axios, category)
      : createNewCategory(axios, category);
    promise.then((updatedCategory) => {
      if (updatedCategory) {
        generateNotification({
          type: NotificationType.SUCCESS,
          value: t('general.notification.success.saveCategory'),
        });
        if (redirect) navigate(-1);
        else setCategory(updatedCategory);
      }
    });
  };

  /**
   * Update translations for the metaInformations
   */
  const handleMetaTranslationUpdate = useCallback(
    (language: Language, value: string): void => {
      if (!metaInfoToEdit) return;
      const translations: Map<Language, string> = metaInfoToEdit.translations;
      translations.set(language, value);
      setMetaInfoToEdit({ ...metaInfoToEdit, translations });
    },
    [metaInfoToEdit],
  );
  /**
   * rows for different translations of the category
   */
  const translationRows: TableRow[] = useMemo(
    (): TableRow[] => generateTableRowForLanguageTranslation(category, 'translations', setCategory),
    [category],
  );

  /**
   * Rows for the translations of an actual meta property
   */
  const metaTranslationRows: TableRow[] = useMemo(
    (): TableRow[] =>
      metaInfoToEdit
        ? generateTranslationTableRows(metaInfoToEdit?.translations, handleMetaTranslationUpdate)
        : [],
    [handleMetaTranslationUpdate, metaInfoToEdit],
  );

  /**
   * Handler method to save a meta information
   */
  const handleSaveMetaInformation = (): void => {
    if (!axios || !metaInfoToEdit || !category) return;

    const informations: InventroyMetaInformation[] = [];
    if (category.informations.some((entry) => entry.id === metaInfoToEdit.id))
      category.informations.map((entry) =>
        entry.id === metaInfoToEdit.id
          ? informations.push(metaInfoToEdit)
          : informations.push(entry),
      );
    else informations.push(...[metaInfoToEdit, ...category.informations]);
    updateCategory(axios, {
      ...category,
      informations,
    }).then((updatedCategory) => updatedCategory && setCategory(updatedCategory));
  };

  /**
   * Rows for the meta overview
   */
  const metaInformationRows: TableRow[] = useMemo((): TableRow[] => {
    return convertMetaInformationIntoTableRows(category.informations, setMetaInfoToEdit);
  }, [category.informations]);
  return (
    <form ref={form} onSubmit={(e) => e.preventDefault()}>
      <TopBar
        onBackClick={() => navigate(-1)}
        children={[<SaveButtons buttonRef={button} handleSubmit={handleSubmit} />]}
        title={t(`pages.inventoryCategory.edit.topBarHeadline${category.id ? 'Edit' : 'Create'}`)}
      />
      <Box>
        <div className="three-columns">
          <Input
            type="text"
            value={category.title}
            label={t('pages.inventoryCategory.edit.name')}
            onChange={(title) => setCategory({ ...category, title })}
          />
          <Input
            type="date"
            value={category.createDate}
            label={t('pages.inventoryCategory.edit.createDate')}
            disabled
          />
          <Input
            type="text"
            value={getUserNameForSimpleUser(category.createdBy, loadedUsers)}
            label={t('pages.inventoryCategory.edit.createdBy')}
            disabled
          />
          <Dropdown
            options={Object.values(InventoryGroup).map((group) => ({
              label: t(`general.inventoryGroup.${group}`),
              value: group,
            }))}
            onChange={(group) => setCategory({ ...category, group: group as InventoryGroup })}
            selectedOption={category.group}
            label={t('pages.inventoryCategory.edit.group')}
          />
          <Input
            label={t('pages.inventoryCategory.edit.checkPeriod')}
            value={category.checkPeriod}
            type="number"
            onChangeNumber={(checkPeriod) => setCategory({ ...category, checkPeriod })}
          />
        </div>
      </Box>
      <Box>
        <Table
          rows={translationRows}
          header={
            t('pages.inventoryCategory.edit.tableHeader', {
              returnObjects: true,
            }) as TableHeader[]
          }
        />
      </Box>
      <Box title={t('pages.inventoryCategory.edit.metaTitle')}>
        <IconComponent
          type={IconType.ADD}
          width={30}
          onClick={() =>
            setMetaInfoToEdit({
              id: uid(),
              title: '',
              translations: new Map<Language, string>(),
              type: InventoryMetaType.TEXT,
            })
          }
        />
        <Table
          rows={metaInformationRows}
          header={
            t('pages.inventoryCategory.edit.metaHeader', {
              returnObjects: true,
            }) as TableHeader[]
          }
        />
        {metaInfoToEdit && (
          <div>
            <Input
              type="text"
              value={metaInfoToEdit.title}
              label={t('pages.inventoryCategory.edit.name')}
              onChange={(title) => setMetaInfoToEdit({ ...metaInfoToEdit, title })}
            />
            <Dropdown
              label={t('pages.inventoryCategory.edit.type')}
              options={Object.values(InventoryMetaType).map((type) => ({
                label: type,
                value: type,
              }))}
              onChange={(type) =>
                setMetaInfoToEdit({
                  ...metaInfoToEdit,
                  type: type as InventoryMetaType,
                })
              }
            />
            <Table
              rows={metaTranslationRows}
              header={
                t('pages.inventoryCategory.edit.tableHeader', {
                  returnObjects: true,
                }) as TableHeader[]
              }
            />
            <Button onClick={handleSaveMetaInformation} value={t('general.buttons.save')} />
          </div>
        )}
      </Box>
    </form>
  );
};
